import { BontonPanel } from './../../../../environments/bonton-panel';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { HolidayService } from 'app/services/holiday.service';
import { CacheService } from 'app/services/cache.service';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { LuxonDateAdapterService } from 'app/services/LuxonDateAdapter.service';
import { CommonUtils, MY_DATE_FORMATS } from 'app/utils/commonutils';
import { SelectPaxComponent } from '../detail/detail-info/select-pax/select-pax.component';
import { SettingsService } from 'app/services/settings.service';
import { themes } from 'app/utils/const';
import { UserService } from 'app/core/user/user.service';
import { CommanService } from 'app/services/comman.service';
import { ValidationService } from 'app/services/validation.service';
import { VideoHelpService } from 'app/services/VideoHelp.service';
import { ToasterService } from 'app/services/toaster.service';

@Component({
    selector: 'app-holidays-main',
    templateUrl: './main.component.html',
    standalone: true,
    imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
    SlickCarouselModule
],
    providers: [
        { provide: DateAdapter, useClass: LuxonDateAdapterService },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    ],
})
export class MainComponent implements OnDestroy {
    @ViewChild('destinationSelect') destinationSelect: MatSelect;

    bontonPanel = BontonPanel;
    formGroup: FormGroup;
    destinationListNew: any[] = [];
    destinationListNewAll: any[] = [];
    minDate: Date = new Date();
    themes = themes;

    slideConfig = {
        arrows: false,
        slidesToShow: 3,
        slidesToScroll: 5,
        dots: true,
        infinite: true,
        autoplay: false,
        autoplaySpeed: 3000,
        variableWidth: true,
        centerMode: false,
        margin: 10,
    };

    topDestinationsList: any[] = [];
    IntDestinationsList: any[] = [];
    DomDestinationsList: any[] = [];
    _unsubscribeAll: Subject<any> = new Subject<any>();
    settings: any = {};
    isOpenPax: boolean;
    wlSettingsDetails: any = {};
    holidaysData: any = {};
    isFirstService: boolean = CommonUtils.getB2COrderByUrl() === 'holidays';

    /**
     * Constructor to initialize required services and listen for settings changes.
     * Subscribes to global settings to update SEO, WL settings, and holiday section data.
     *
     * @param {Router} router - Used for route navigation within the module.
     * @param {FormBuilder} builder - Helps create and manage reactive form groups.
     * @param {HolidayService} holidayService - Handles holiday-related API calls.
     * @param {MatDialog} matDialog - Opens material dialogs (e.g., Select Pax popup).
     * @param {SettingsService} settingService - Provides global settings and SEO meta updates.
     * @param {CacheService} cacheService - Used for caching reusable data.
     * @param {UserService} _userService - Provides logged-in user details and session info.
     * @param {CommanService} commanService - Shared utilities and UI helpers (loader, etc).
     * @param {ValidationService} validationService - Custom validation helpers for forms.
     */
    constructor(
        private router: Router,
        private builder: FormBuilder,
        private holidayService: HolidayService,
        private matDialog: MatDialog,
        private settingService: SettingsService,
        private cacheService: CacheService,
        private _userService: UserService,
        private commanService: CommanService,
        private validationService: ValidationService,
        private videoHelpService: VideoHelpService,
    ) {
        // Subscribe to global UI & SEO settings; keep updating until component destroy.
        this.settingService.settings.pipe(takeUntil(this._unsubscribeAll)).subscribe(settings => {
            this.settings = settings;
            this.wlSettingsDetails = settings;
            this.holidaysData = this.wlSettingsDetails?.service_search_sections?.find(x => x.section_for.toLowerCase() == "holiday");
            this.settingService.updateSeoPageMeta('holidays'); // Seo Page Meta Details Updates
        });
    }

    /**
     * Angular lifecycle hook - Initializes component data and form setup.
     * Loads destination lists, sets default form values, and restores saved filters from localStorage.
     * @returns {Promise<void>} A promise that resolves once initialization is complete.
     */
    async ngOnInit(): Promise<void> {

        // Default date set to 20 days from today
        const defaultDate = DateTime.now().plus({ days: 20 }).toFormat('yyyy-MM-dd');

        // Initialize form controls
        this.formGroup = this.builder.group({
            id: [''],
            departure_date: defaultDate,
            destination_id: [''],
            adult: [2],
            child: [0],
            destinationfilter: [''],
            destinationfilterNew: [''],
        });

        // Load B2B-specific data if applicable
        if (this.bontonPanel.isB2B()) {
            this.getdestinationRecords();
            this.getTopDestination();
        }

        // Fetch destinations and set defaults
        this.holidayService.getDestinations('').subscribe({
            next: (data) => {
                this.destinationListNew = data;
                this.destinationListNewAll = data;
                const oldJSON: any = JSON.parse(localStorage.getItem('holiday-filters'));
                if (!oldJSON) {
                    this.formGroup.get('destination_id').patchValue(this.destinationListNew[0]);
                }
            },
            error: (err) => {
                // Handle API errors if needed
            }
        });

        // Filter destination results based on user input
        this.formGroup.get('destinationfilterNew').valueChanges.subscribe((data) => {
            const trimmedData = data?.trim().toLowerCase(); // Trim and convert to lowercase
            this.destinationListNew = this.destinationListNewAll.filter((x) =>
                x.name.toLowerCase().includes(trimmedData)
            );
        });

        // Restore previous filter data from local storage
        const oldJSON: any = JSON.parse(localStorage.getItem('holiday-filters'));
        if (oldJSON) {
            if (new Date(oldJSON.departure_date) < new Date()) {
                oldJSON.departure_date = DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd');
            }
            this.formGroup.patchValue(oldJSON);
            this.formGroup.get('destinationfilterNew').patchValue(oldJSON.destination_id.destination_name);
        }
    }

    /**
     * Opens a new browser tab or window with the specified URL.
     * @param {string} str - The URL to open.
     * @returns {void}
     */
    openNewWindow(str: string): void {
        window.open(str, '_blank');
    }

    /**
     * Angular lifecycle hook - Cleans up subscriptions and memory when component is destroyed.
     * @returns {void}
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Fetches and categorizes destinations as domestic or international.
     * Updates `IntDestinationsList` and `DomDestinationsList` accordingly.
     * @returns {void}
     */
    getdestinationRecords(): void {
        this.holidayService.IntDomDestinations().subscribe((res: any[]) => {
            this.IntDestinationsList = res.filter(x => !x.is_domestic);
            this.DomDestinationsList = res.filter(x => x.is_domestic);
        });
    }

    /**
     * Retrieves the top holiday destinations for display.
     * Updates the `topDestinationsList` property.
     * @returns {void}
     */
    getTopDestination(): void {
        this.holidayService.topDestinations().subscribe((res: any) => {
            this.topDestinationsList = Array.isArray(res) ? res : [];
        });
    }

    /**
     * Handles selection of a top destination and opens detail view.
     * @param {any} val - The selected top destination object.
     * @returns {void}
     */
    topDestination(val): void {
        val.text = 'recentSearch';
        this.detail(val);
    }

    /**
     * Navigates to the detail page for the selected destination.
     * Handles route formatting and updates cache with selected filters.
     * @param {any} [val] - Optional object representing a selected destination.
     * @returns {void}
     */
    detail(val?: any): void {
        const json: any = { ...this.formGroup.getRawValue() };
        json.departure_date = DateTime.fromISO(String(json.departure_date)).toFormat('yyyy-MM-dd');

        // Handle recent search scenario
        if (val?.text === 'recentSearch') {
            json.destination_id = { destination_id: val.id, destination_name: val.destination_name, type: 'Destination', id: val.id };
            json.filter = val.destination_name;
            json.adult = 2; json.child = 0;
        }

        this.cacheService.MainFilterChanged(json, 'holiday-filters');

        // Clean up and format destination name for navigation (SEO-friendly)
        let destinationName = String(json?.destination_id?.name || json?.destination_id?.destination_name || '');
        if (destinationName.includes('-')) { destinationName = this.validationService.replaceDashedWithDoubleUnderscore(destinationName); }
        destinationName = this.validationService.removeSpecialCharacter(destinationName);

        // Construct navigation URL
        let navigateStr = `/holidays/detail/${destinationName}/${json.departure_date}/${json.adult}/${json.child}`;
        navigateStr = this.validationService.replaceSpaceToHyphen(navigateStr);
        this.router.navigate([navigateStr]);
    }


    /**
     * Compares two destination objects for equality in selection controls.
     * @param {any} v1 - The first object to compare.
     * @param {any} v2 - The second object to compare.
     * @returns {boolean} True if both IDs and types match; otherwise, false.
     */
    public compareWith(v1: any, v2: any): boolean {
        return v1 && v2 && v1.id === v2.id && v1.type === v2.type;
    }

    /**
     * Opens a dialog for selecting the number of travelers (adults and children).
     * Updates form fields based on dialog result.
     * @returns {void}
     */
    travellersClass(): void {
        this.isOpenPax = true;
        const { adult, child } = this.formGroup.value as any;

        this.matDialog.open(SelectPaxComponent, { data: { adult, child }, disableClose: true })
            .afterClosed()
            .subscribe(res => {
                this.isOpenPax = false;
                if (!res) return;
                if (adult === res.adult && child === res.child) return;
                this.formGroup.patchValue({ adult: res.adult, child: res.child });
            });
    }


    /**
     * Opens the destination select dropdown programmatically.
     * @returns {void}
     */
    openDestination(): void {
        this.destinationSelect.open();
    }

    helpVideo(sectionName: string) {
        this.videoHelpService.helpVideo(sectionName);
    }
}
